import { useMediaQuery } from "@mui/material";
import { FC, useEffect, useState } from "react";

import Button from "../../../../components/Button";
import DatePicker from "../../../../components/DatePicker";
import Switch from "../../../../components/Switch";
import Text from "../../../../components/Text";
import { getAllRoles } from "../../../../services/api/admin/role";
import { getAllTags } from "../../../../services/api/admin/tags";
import { Tag } from "../../../../types/api/admin/tag";
import { Role } from "../../../../types/Role";
import { StatsFiltersState } from "../types";
import FilterField from "./FilterField";
import { CHALLENGE_OPTIONS } from "./filterOptions";
import { TIMESLOT_FILTER_OPTIONS } from "../../../../utils/timeslots";
import styles from "./styles.module.scss";

type StatsFiltersProps = {
    filters: StatsFiltersState;
    pickerStartDate: Date;
    pickerEndDate: Date;
    onUpdateFilters: (updates: Partial<StatsFiltersState>) => void;
    onEndDateChange: (date: Date) => void;
    onResetFilters: () => void;
};

const StatsFilters: FC<StatsFiltersProps> = ({
    filters,
    pickerStartDate,
    pickerEndDate,
    onUpdateFilters,
    onEndDateChange,
    onResetFilters,
}) => {
    const isMobile = useMediaQuery("(max-width: 1050px)");
    const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
    const [availableTags, setAvailableTags] = useState<Tag[]>([]);

    useEffect(() => {
        const fetchTags = async () => {
            try {
                const tags = await getAllTags();
                setAvailableTags(tags || []);
            } catch (error) {
                console.error("Erreur lors du chargement des tags", error);
                setAvailableTags([]);
            }
        };

        const fetchRoles = async () => {
            try {
                const roles = await getAllRoles();
                setAvailableRoles(roles);
            } catch (error) {
                console.error("Erreur lors du chargement des rôles", error);
                setAvailableRoles([]);
            }
        };

        fetchTags();
        fetchRoles();
    }, []);

    return (
        <div className={styles.filters}>
            <div className={styles.additionalFilters}>
                <div className={styles.selectRow}>
                    <div className={styles.datePickerContainer}>
                        <Text variant="body-bold">Créneau de dates</Text>
                        <DatePicker
                            mode="range"
                            startDate={pickerStartDate}
                            setStartDate={(date) =>
                                onUpdateFilters({ startDate: date })
                            }
                            endDate={pickerEndDate}
                            setEndDate={onEndDateChange}
                            placement="bottom-start"
                            classNameTrigger={styles.datePickerTrigger}
                        />
                    </div>
                    <FilterField
                        label="Créneaux"
                        options={TIMESLOT_FILTER_OPTIONS}
                        value={filters.timeslotFilter}
                        onChange={(value) =>
                            onUpdateFilters({ timeslotFilter: value })
                        }
                    />
                    <FilterField
                        label="Challenges"
                        options={CHALLENGE_OPTIONS}
                        value={filters.challengeFilter}
                        onChange={(value) =>
                            onUpdateFilters({ challengeFilter: value })
                        }
                    />
                    {availableRoles.length > 0 && (
                        <FilterField
                            label="Rôles"
                            options={availableRoles.map((role) => ({
                                label: role.name,
                                value: role.id,
                            }))}
                            value={filters.selectedRoles[0] || ""}
                            onChange={(value) =>
                                onUpdateFilters({
                                    selectedRoles: value ? [value] : [],
                                })
                            }
                            isMultiple
                        />
                    )}
                    {availableTags.length > 0 && (
                        <FilterField
                            label="Tags"
                            options={availableTags.map((tag) => ({
                                label: tag.name,
                                value: tag.id,
                            }))}
                            value={filters.selectedTags[0] || ""}
                            onChange={(value) =>
                                onUpdateFilters({
                                    selectedTags: value ? [value] : [],
                                })
                            }
                            isMultiple
                        />
                    )}
                    <Switch
                        label="Favoris"
                        checked={filters.favoritesOnly}
                        onChange={(checked) =>
                            onUpdateFilters({ favoritesOnly: checked })
                        }
                    />
                    {filters.hasFiltersBeenModified && (
                        <Button
                            size="medium"
                            variant="secondary"
                            onClick={onResetFilters}
                            label={
                                isMobile
                                    ? "Reset"
                                    : "Réinitialiser les filtres"
                            }
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default StatsFilters;
